// ProjectAddress.jsx
// Standalone address component with:
//  - Nominatim place search autocomplete (India only)
//  - Leaflet map with click-to-pin + my location
//  - Auto-filled address fields (street, locality, city, state, pincode)
//  - No location type, no country field

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, IconButton, Tooltip,
  Paper, List, ListItem, ListItemText, CircularProgress,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EditLocationAltOutlinedIcon from '@mui/icons-material/EditLocationAltOutlined';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import MarkunreadMailboxOutlinedIcon from '@mui/icons-material/MarkunreadMailboxOutlined';
import ClearIcon from '@mui/icons-material/Clear';

// ── colour palette (matches parent form) ─────────────────────────────────────
const C = {
  blue:      '#1565c0',
  blueLight: 'rgba(21,101,192,0.10)',
  blueBg:    'rgba(21,101,192,0.04)',
  green:     '#2e7d32',
  greenLight:'rgba(46,125,50,0.10)',
  sub:       '#666',
  text:      '#1a1a1a',
  border:    '#e0e0e0',
};

const mkFs = (accent = C.blue) => ({
  '& .MuiInputBase-root': { height: 40, fontSize: 12.5, borderRadius: 1.5 },
  '& .MuiInputLabel-root': { fontSize: 12, color: C.sub, '&.Mui-focused': { color: accent } },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accent, borderWidth: 1.5 },
  },
  '& .MuiInputBase-input::placeholder': { fontSize: 12, opacity: 0.6 },
});

// ── Debounce hook ──────────────────────────────────────────────────────────────
const useDebounce = (value, delay = 350) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// ── Build a display label from a Nominatim result ─────────────────────────────
const buildLabel = (r) => {
  const a = r.address || {};
  const parts = [
    a.road || a.neighbourhood || a.suburb,
    a.city || a.town || a.village || a.county,
    a.state,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : r.display_name;
};

// ── Extract a meaningful street/address line from Nominatim data ──────────────
const buildAddressLine = (addr, resultName) => {
  const placeName = addr.building || addr.amenity || addr.office || addr.shop
    || addr.tourism || addr.leisure || addr.industrial || addr.commercial || '';
  const houseNum = addr.house_number || '';
  const road = addr.road || '';

  const parts = [placeName, houseNum, road].map(s => (s || '').trim()).filter(Boolean);
  if (parts.length) return parts.join(', ');

  // Fall back to the top-level result name if address fields are sparse
  if (resultName) {
    const neighbourhood = addr.neighbourhood || addr.suburb || '';
    return [resultName, neighbourhood].filter(Boolean).join(', ');
  }
  return '';
};

// ── Extract locality from Nominatim address ──────────────────────────────────
const buildLocality = (addr) =>
  addr.suburb || addr.neighbourhood || addr.quarter
  || addr.city_district || addr.village || '';

// ── Map PIN icon (coloured) ──────────────────────────────────────────────────
const buildIcon = (L, color = C.blue) =>
  L.divIcon({
    html: `<div style="
      width:28px;height:28px;
      background:${color};
      border:3px solid white;
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      box-shadow:0 3px 10px rgba(0,0,0,0.30);
    "></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    className: '',
  });

// ═════════════════════════════════════════════════════════════════════════════
// ProjectAddress
// Props:
//   value   : address object { address_line, locality, city, state, pincode, latitude, longitude, google_place_id }
//   onChange: (addressObj) => void
//   error   : string | undefined   (shown under City field)
// ═════════════════════════════════════════════════════════════════════════════
const ProjectAddress = ({ value = {}, onChange, error }) => {
  // ── search state ───────────────────────────────────────────────────────────
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen]           = useState(false);
  const debouncedQuery            = useDebounce(query, 400);
  const searchRef                 = useRef(null);

  // ── map state ─────────────────────────────────────────────────────────────
  const mapContainerRef = useRef(null);
  const leafletMapRef   = useRef(null);
  const markerRef       = useRef(null);
  const [mapReady, setMapReady]   = useState(false);
  const [locating, setLocating]   = useState(false);

  const addr = value || {};

  // ── Load Leaflet lazily ───────────────────────────────────────────────────
  useEffect(() => {
    if (window.L) { setMapReady(true); return; }
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
    script.onload = () => setMapReady(true);
    document.head.appendChild(script);
  }, []);

  // ── Init map ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady || !mapContainerRef.current || leafletMapRef.current) return;

    const L   = window.L;
    const map = L.map(mapContainerRef.current, {
      center: [20.5937, 78.9629],
      zoom: 5,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    map.on('click', async (e) => {
      const { lat, lng } = e.latlng;
      placeMarker(map, L, lat, lng);
      await reverseGeocode(lat, lng);
    });

    leafletMapRef.current = map;

    // If address already has coords, fly there & place marker
    if (addr.latitude && addr.longitude) {
      map.setView([addr.latitude, addr.longitude], 14);
      placeMarker(map, L, addr.latitude, addr.longitude);
    } else if (addr.city) {
      flyToCity(map, addr.city);
    }

    return () => {
      map.remove();
      leafletMapRef.current = null;
      markerRef.current     = null;
    };
  }, [mapReady]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const placeMarker = (map, L, lat, lng) => {
    const icon = buildIcon(L, C.blue);
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
      markerRef.current.setIcon(icon);
    } else {
      markerRef.current = L.marker([lat, lng], { icon }).addTo(map);
    }
  };

  const flyToCity = (map, city) => {
    fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city)},India&format=json&limit=1`,
      { headers: { 'User-Agent': 'SalesGro App' } }
    )
      .then(r => r.json())
      .then(d => {
        if (d[0] && map) map.flyTo([parseFloat(d[0].lat), parseFloat(d[0].lon)], 13, { duration: 1 });
      })
      .catch(() => {});
  };

  // Reverse geocode → fill address fields
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const res  = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
        { headers: { 'User-Agent': 'SalesGro App' } }
      );
      const data = await res.json();
      const a    = data.address || {};
      onChange({
        ...(value || {}),
        address_line:    buildAddressLine(a, data.name) || addr.address_line || '',
        locality:        buildLocality(a) || addr.locality || '',
        city:            a.city || a.town || a.county || addr.city || '',
        state:           a.state || addr.state || '',
        pincode:         a.postcode || addr.pincode || '',
        latitude:        lat,
        longitude:       lng,
        google_place_id: data.place_id?.toString() || addr.google_place_id || '',
      });
    } catch {
      onChange({ ...(value || {}), latitude: lat, longitude: lng });
    }
  }, [value, onChange]);

  // Apply a Nominatim search result (fly map + fill fields)
  const applyResult = useCallback((result) => {
    const a   = result.address || {};
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    onChange({
      ...(value || {}),
      address_line:    buildAddressLine(a, result.name || result.display_name?.split(',')[0]) || '',
      locality:        buildLocality(a) || '',
      city:            a.city || a.town || a.county || '',
      state:           a.state || '',
      pincode:         a.postcode || '',
      latitude:        lat,
      longitude:       lng,
      google_place_id: result.place_id?.toString() || '',
    });

    const map = leafletMapRef.current;
    const L   = window.L;
    if (map && L) {
      map.flyTo([lat, lng], 14, { duration: 1 });
      placeMarker(map, L, lat, lng);
    }

    setQuery('');
    setOpen(false);
    setResults([]);
  }, [value, onChange]);

  // My location
  const handleMyLocation = () => {
    if (!navigator.geolocation || !leafletMapRef.current) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude: lat, longitude: lng } }) => {
        const map = leafletMapRef.current;
        const L   = window.L;
        map.flyTo([lat, lng], 15, { duration: 1.2 });
        placeMarker(map, L, lat, lng);
        await reverseGeocode(lat, lng);
        setLocating(false);
      },
      () => setLocating(false),
      { timeout: 8000 }
    );
  };

  // ── Nominatim search ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setSearching(true);

    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(debouncedQuery.trim())}&format=json&addressdetails=1&limit=10&countrycodes=in`;

    fetch(url, { headers: { 'User-Agent': 'SalesGro App' } })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        setResults(data || []);
        setOpen((data || []).length > 0);
      })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setSearching(false); });

    return () => { cancelled = true; };
  }, [debouncedQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Field updater
  const setField = (field, val) => onChange({ ...(value || {}), [field]: val });

  const hasPin = !!(addr.latitude && addr.longitude);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ── Search bar ──────────────────────────────────────────────────── */}
      <Box ref={searchRef} sx={{ position: 'relative' }}>
        <TextField
          fullWidth
          placeholder="Search city, area or address in India…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); if (!e.target.value) setOpen(false); }}
          size="small"
          autoComplete="off"
          sx={{
            '& .MuiInputBase-root': {
              height: 42, fontSize: 12.5, borderRadius: 2,
              bgcolor: C.blueBg,
              border: `1.5px solid ${C.blue}30`,
              transition: 'border-color 0.2s',
            },
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.blue, borderWidth: 1.5 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: `${C.blue}30` },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching
                  ? <CircularProgress size={14} sx={{ color: C.blue }} />
                  : <SearchIcon sx={{ fontSize: 17, color: C.blue }} />
                }
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setQuery(''); setOpen(false); setResults([]); }}>
                  <ClearIcon sx={{ fontSize: 14, color: C.sub }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Dropdown */}
        {open && results.length > 0 && (
          <Paper
            elevation={6}
            sx={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
              zIndex: 1400, maxHeight: 240, overflow: 'auto',
              borderRadius: 2, border: `1px solid ${C.border}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }}
          >
            <List dense disablePadding>
              {results.map((r, idx) => {
                const primary   = r.address?.city || r.address?.town || r.address?.village || r.address?.county || r.name;
                const secondary = [r.address?.suburb || r.address?.neighbourhood, r.address?.state].filter(Boolean).join(', ') || r.display_name;
                return (
                  <ListItem
                    key={r.place_id || idx}
                    button
                    onClick={() => applyResult(r)}
                    sx={{
                      py: 0.9, px: 1.5,
                      borderBottom: idx < results.length - 1 ? `1px solid ${C.border}` : 'none',
                      '&:hover': { bgcolor: C.blueBg },
                      cursor: 'pointer',
                      gap: 1,
                    }}
                  >
                    <LocationOnOutlinedIcon sx={{ fontSize: 15, color: C.blue, flexShrink: 0, mt: 0.1 }} />
                    <ListItemText
                      primary={primary}
                      secondary={secondary}
                      primaryTypographyProps={{ fontSize: 12.5, fontWeight: 600, color: C.text }}
                      secondaryTypographyProps={{ fontSize: 10.5, color: C.sub, noWrap: true }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>

      {/* ── Map ─────────────────────────────────────────────────────────── */}
      {mapReady ? (
        <Box sx={{
          position: 'relative', borderRadius: 2, overflow: 'hidden',
          border: '1px solid', borderColor: C.border,
          boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
        }}>
          <Box ref={mapContainerRef} sx={{ height: 210, width: '100%' }} />

          {/* Hint pill */}
          <Box sx={{
            position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)',
            bgcolor: 'rgba(255,255,255,0.93)', backdropFilter: 'blur(4px)',
            px: 1.5, py: 0.35, borderRadius: 10,
            boxShadow: '0 1px 6px rgba(0,0,0,0.10)',
            pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 999,
          }}>
            <Typography sx={{ fontSize: 10, color: C.sub, fontWeight: 500 }}>
              {hasPin ? '📍 Pin placed — drag or click to update' : 'Click map to drop a pin'}
            </Typography>
          </Box>

          {/* My location button */}
          <Tooltip title="Use my current location" placement="left">
            <Box sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 1000 }}>
              <IconButton
                onClick={handleMyLocation}
                disabled={locating}
                size="small"
                sx={{
                  bgcolor: 'white',
                  boxShadow: '0 1px 6px rgba(0,0,0,0.18)',
                  border: '1px solid', borderColor: C.border,
                  '&:hover': { bgcolor: C.blueLight },
                }}
              >
                {locating
                  ? <CircularProgress size={14} sx={{ color: C.blue }} />
                  : <MyLocationIcon sx={{ fontSize: 15, color: C.blue }} />
                }
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{
          height: 210, borderRadius: 2,
          bgcolor: C.blueBg, border: '1px dashed', borderColor: `${C.blue}40`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} sx={{ color: C.blue }} />
            <Typography sx={{ fontSize: 11, color: C.sub }}>Loading map…</Typography>
          </Box>
        </Box>
      )}

      {/* GPS coordinates badge */}
      {hasPin && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          px: 1.25, py: 0.6, borderRadius: 1.5,
          bgcolor: C.blueLight, border: `1px solid ${C.blue}20`,
        }}>
          <LocationOnOutlinedIcon sx={{ fontSize: 13, color: C.blue }} />
          <Typography sx={{ fontSize: 10, color: C.blue, fontWeight: 700 }}>
            {Number(addr.latitude).toFixed(6)}, {Number(addr.longitude).toFixed(6)}
          </Typography>
          <Typography sx={{ fontSize: 10, color: C.sub, ml: 0.25 }}>· GPS coordinates saved</Typography>
        </Box>
      )}

      {/* ── Address fields ── real-world order: Street → Locality/City → State/Pincode ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>

        {/* Street */}
        <TextField
          fullWidth
          label="Street / Address line"
          placeholder="e.g. 12, MG Road, Near Metro Station"
          value={addr.address_line || ''}
          onChange={(e) => setField('address_line', e.target.value)}
          size="small"
          sx={mkFs(C.blue)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EditLocationAltOutlinedIcon sx={{ fontSize: 15, color: '#bbb' }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Google Maps URL (required) */}
        <TextField
          fullWidth
          required
          label="Google Maps link"
          placeholder="Paste the Google Maps URL for this location"
          value={addr.google_maps_url || ''}
          onChange={(e) => setField('google_maps_url', e.target.value)}
          size="small"
          sx={mkFs(C.blue)}
        />

        {/* Locality | City */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
          <TextField
            fullWidth
            label="Locality / Area"
            placeholder="e.g. Koramangala"
            value={addr.locality || ''}
            onChange={(e) => setField('locality', e.target.value)}
            size="small"
            sx={mkFs(C.blue)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOnOutlinedIcon sx={{ fontSize: 15, color: '#bbb' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            required
            label="City"
            placeholder="e.g. Mumbai"
            value={addr.city || ''}
            onChange={(e) => setField('city', e.target.value)}
            error={!!error}
            helperText={error}
            size="small"
            sx={mkFs(C.blue)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PinDropOutlinedIcon sx={{ fontSize: 15, color: '#bbb' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* State | Pincode */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
          <TextField
            fullWidth
            label="State"
            placeholder="e.g. Maharashtra"
            value={addr.state || ''}
            onChange={(e) => setField('state', e.target.value)}
            size="small"
            sx={mkFs(C.blue)}
          />
          <TextField
            fullWidth
            label="Pincode"
            placeholder="e.g. 400063"
            value={addr.pincode || ''}
            onChange={(e) => setField('pincode', e.target.value)}
            size="small"
            sx={mkFs(C.blue)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MarkunreadMailboxOutlinedIcon sx={{ fontSize: 15, color: '#bbb' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectAddress;
